import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import AdminProductsApi from "./api/adminProductsApi";
import { AdminProductsState, ChangeProduct, ProductsQuery, ProductsQueryParameter } from "./models/AdminProductsModels";
import adminManufacturersApi from "./api/adminManufacturersApi";
import adminCategoriesApi from "./api/adminCategoriesApi";
import { FileModel } from "../shared/models/models";

export const getProducts = createAsyncThunk(
  "adminProducts/getProducts",
  async (query: ProductsQuery) => {
    return AdminProductsApi.getProducts(query);
  }
);

export const createProduct = createAsyncThunk(
  "adminProducts/createProduct",
  async (model: ChangeProduct) => {
    return AdminProductsApi.createProduct(model);
  }
);

export const createProductFromFile = createAsyncThunk(
  "adminProducts/createProductFromFile",
  async (model: FileModel) => {
    return AdminProductsApi.createProductFromFile(model);
  }
);

export const changeProduct = createAsyncThunk(
  "adminProducts/changeProduct",
  async ({ id, model }: any) => {
    return AdminProductsApi.changeProduct(id, model);
  }
);

export const deleteProduct = createAsyncThunk(
  "adminProducts/deleteProduct",
  async (id: string) => {
    return AdminProductsApi.deleteProduct(id);
  }
);

export const getManufacturers = createAsyncThunk(
  "adminProducts/getManufacturers",
  async () => {
    return adminManufacturersApi.getManufacturers();
  }
);

export const getCategories = createAsyncThunk(
  "adminProducts/getCategories",
  async () => {
    return adminCategoriesApi.getCategories();
  }
);

const initialState: AdminProductsState = {
  query: {
    search: undefined,
    sortBy: ProductsQueryParameter.CreatedDate,
    sortDesc: true,
    pageNo: 1,
    pageSize: 20
  },
  products: undefined,
  product: undefined,
  manufacturers: [],
  categories: [],
  loading: false,
  success: undefined,
  error: undefined,
  fileResult: undefined
};

const adminProductsSlice = createSlice({
  name: "adminProducts",
  initialState: initialState,
  reducers: {
    changeQuery: (state, { payload }) => {
      state.query = payload;
    },
    selectProduct: (state, action) => {
      state.product = action.payload;
    },
    setBatchResult: (state, { payload }) => {
      state.fileResult = payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProducts.fulfilled, (state, { payload }) => {
        state.products = payload;
        state.loading = false;
      })
      .addCase(getProducts.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProduct.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(createProduct.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(createProductFromFile.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProductFromFile.fulfilled, (state, { payload }) => {
        state.fileResult = payload;
        state.loading = false;
      })
      .addCase(createProductFromFile.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(changeProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeProduct.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(changeProduct.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProduct.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(deleteProduct.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getManufacturers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getManufacturers.fulfilled, (state, { payload }) => {
        state.manufacturers = payload;
        state.loading = false;
      })
      .addCase(getManufacturers.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCategories.fulfilled, (state, { payload }) => {
        state.categories = payload;
        state.loading = false;
      })
      .addCase(getCategories.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });
  }
});

export const {
  changeQuery,
  selectProduct,
  setBatchResult,
  setSuccess,
  setError
} = adminProductsSlice.actions;

export default adminProductsSlice.reducer;